import { isInCheck, allLegalMoves, applyMove } from "./moves";
import type { GameState, Move, PieceType } from "./types";

export type AiDifficulty = "easy" | "normal" | "hard";

const DEPTH_BY_DIFFICULTY: Record<AiDifficulty, number> = { easy: 1, normal: 2, hard: 4 };
/** "easy" joue parfois un coup legal au hasard plutot que le meilleur trouve, pour rester battable. */
const RANDOM_MOVE_CHANCE: Record<AiDifficulty, number> = { easy: 0.35, normal: 0.08, hard: 0 };

const PIECE_VALUES: Record<PieceType, number> = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };
const MATE_SCORE = 1_000_000;

/** Score du point de vue des Blancs (positif = avantage blanc) : materiel + leger bonus de controle du centre. */
function evaluate(state: GameState): number {
  let score = 0;
  state.board.forEach((piece, square) => {
    if (!piece) return;
    const file = square & 7;
    const rank = square >> 3;
    const centerBonus = 4 - (Math.abs(file - 3.5) + Math.abs(rank - 3.5));
    const value = PIECE_VALUES[piece.type] + centerBonus * 2;
    score += piece.color === "w" ? value : -value;
  });
  return score;
}

/** Trie les captures en premier (approximation MVV) pour un elagage alpha-beta plus efficace. */
function orderMoves(moves: readonly Move[]): Move[] {
  return [...moves].sort((a, b) => {
    const aVal = a.captured ? PIECE_VALUES[a.captured.type] : -1;
    const bVal = b.captured ? PIECE_VALUES[b.captured.type] : -1;
    return bVal - aVal;
  });
}

/** Negamax + elagage alpha-beta. Retourne un score du point de vue du joueur au trait dans `state`. */
function negamax(state: GameState, depth: number, alpha: number, beta: number): number {
  const moves = allLegalMoves(state);
  if (moves.length === 0) {
    return isInCheck(state) ? -MATE_SCORE - depth : 0;
  }
  if (depth === 0) {
    return evaluate(state) * (state.turn === "w" ? 1 : -1);
  }

  let best = -Infinity;
  for (const move of orderMoves(moves)) {
    const next = applyMove(state, move);
    const score = -negamax(next, depth - 1, -beta, -alpha);
    if (score > best) best = score;
    if (score > alpha) alpha = score;
    if (alpha >= beta) break;
  }
  return best;
}

function randomChoice<T>(items: readonly T[]): T {
  const item = items[Math.floor(Math.random() * items.length)];
  if (item === undefined) throw new Error("randomChoice sur un tableau vide");
  return item;
}

/** Recherche pure (sans alea) : le(s) meilleur(s) coup(s) pour le joueur au trait, a la profondeur donnee. */
export function findBestMove(state: GameState, depth: number): Move | null {
  const moves = allLegalMoves(state);
  if (moves.length === 0) return null;

  let bestMoves: Move[] = [];
  let bestScore = -Infinity;
  for (const move of orderMoves(moves)) {
    const next = applyMove(state, move);
    const score = -negamax(next, depth - 1, -Infinity, Infinity);
    if (score > bestScore) {
      bestScore = score;
      bestMoves = [move];
    } else if (score === bestScore) {
      bestMoves.push(move);
    }
  }
  return randomChoice(bestMoves);
}

/** Choisit un coup pour le joueur au trait dans `state`, selon la difficulte. */
export function chooseAiMove(state: GameState, difficulty: AiDifficulty): Move | null {
  const moves = allLegalMoves(state);
  if (moves.length === 0) return null;

  if (Math.random() < RANDOM_MOVE_CHANCE[difficulty]) {
    return randomChoice(moves);
  }

  return findBestMove(state, DEPTH_BY_DIFFICULTY[difficulty]);
}
